import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaLeaf, FaHeart, FaShieldAlt, FaUsers, FaStar, FaMapMarkedAlt } from 'react-icons/fa';
import { GiFlowerPot } from 'react-icons/gi';

const WhyChoose = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const reasons = [
    {
      icon: <FaLeaf />,
      title: "Eco-Friendly Stay",
      description: "We practice sustainable tourism with organic gardens, rainwater harvesting, and minimal plastic use",
      color: "from-green-400 to-green-600"
    },
    {
      icon: <FaHeart />,
      title: "Warm Hospitality",
      description: "Experience genuine warmth and personalized care that makes you feel like family",
      color: "from-red-400 to-red-600"
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Prime Location",
      description: "Strategic location with easy access to major attractions while maintaining tranquility",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: <FaShieldAlt />,
      title: "Safe & Secure",
      description: "24/7 security, CCTV surveillance, and well-lit premises ensure your safety",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: <FaStar />,
      title: "Value for Money",
      description: "Premium amenities and exceptional service at competitive prices with no hidden costs",
      color: "from-yellow-400 to-yellow-600"
    },
    {
      icon: <FaUsers />,
      title: "Perfect for All",
      description: "Ideal for couples, families, solo travelers, and groups - everyone is welcome",
      color: "from-pink-400 to-pink-600"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-primary-50 to-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <GiFlowerPot className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Choose Navyaa?
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover what sets us apart and makes your stay truly special
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="relative group"
            >
              {/* Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all h-full">
                {/* Icon with Gradient */}
                <motion.div
                  className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-xl flex items-center justify-center text-white text-3xl mb-6 group-hover:scale-110 transition-transform`}
                  whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  {reason.icon}
                </motion.div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {reason.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {reason.description}
                </p>

                {/* Decorative Corner */}
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${reason.color} opacity-5 rounded-bl-full transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-500`}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Features Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-16 bg-gradient-to-r from-primary-600 via-nature-leaf to-primary-600 text-white rounded-2xl p-8 md:p-12 relative overflow-hidden"
        >
          {/* Animated Background Elements */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -mr-48 -mt-48"
          ></motion.div>
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
            className="absolute bottom-0 left-0 w-80 h-80 bg-white rounded-full -ml-40 -mb-40"
          ></motion.div>

          <div className="relative z-10 text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Experience the Difference
            </h3>
            <p className="text-lg md:text-xl mb-6 max-w-2xl mx-auto opacity-90">
              Join hundreds of satisfied guests who have made Navyaa their preferred choice 
              for a memorable getaway
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#booking" 
                className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-full font-bold inline-block transition-all hover:scale-105 shadow-lg"
              >
                Book Your Stay Now
              </a>
              <a 
                href="#contact" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-4 rounded-full font-bold inline-block transition-all hover:scale-105"
              >
                Have Questions?
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChoose;
